import { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

export function useDashboardAnalytics(organizationId) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState({
    topParties: [],
    topSalespeople: [],
    totalOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    monthlyOrders: [],
    monthlyRevenue: [],
    months: [],
    partyLastOrderDates: [],
    topRevenueParties: [],
    topRevenueSalespeople: [],
    previousMonthOrders: 0,
    previousMonthRevenue: 0,
    previousMonthAOV: 0,
    currentMonthOrders: 0,
    currentMonthRevenue: 0,
    currentMonthAOV: 0,
    dailyOrders: [],
    dailyRevenue: [],
    dailyAOV: [],
    ordersRaw: []
  });
  const [userMap, setUserMap] = useState({}); // userId -> fullName

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        // Fetch users for the organization
        const usersRef = collection(db, 'organizations', organizationId, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const userMapTemp = {};
        usersSnapshot.forEach((doc) => {
          const data = doc.data();
          userMapTemp[doc.id] = data.fullName || doc.id;
        });
        setUserMap(userMapTemp);

        // Fetch orders for the organization
        const ordersRef = collection(db, 'organizations', organizationId, 'orders');
        const q = query(ordersRef);
        const querySnapshot = await getDocs(q);
        const orders = [];
        const partyStats = {};
        const partyRevenueStats = {};
        const salespersonStats = {};
        const salespersonRevenueStats = {};
        const statusCounts = {};
        const monthlyData = {};
        const partyLastOrderMap = {};

        // Period comparison data
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;

        let currentMonthOrders = 0;
        let currentMonthRevenue = 0;
        let previousMonthOrders = 0;
        let previousMonthRevenue = 0;

        // Daily data for sparklines (last 7 days)
        const dailyData = {};
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateKey = date.toISOString().split('T')[0];
          dailyData[dateKey] = { orders: 0, revenue: 0, count: 0 };
        }

        querySnapshot.forEach((doc) => {
          const order = { id: doc.id, ...doc.data() };
          orders.push(order);

          const orderValue = Number(order.orderValue || 0);
          const orderDate = order.createdAt.toDate();
          const orderMonth = orderDate.getMonth();
          const orderYear = orderDate.getFullYear();

          // Period comparison
          if (orderMonth === currentMonth && orderYear === currentYear) {
            currentMonthOrders++;
            currentMonthRevenue += orderValue;
          } else if (orderMonth === previousMonth && orderYear === previousYear) {
            previousMonthOrders++;
            previousMonthRevenue += orderValue;
          }

          // Daily data for sparklines
          const orderDateKey = orderDate.toISOString().split('T')[0];
          if (dailyData[orderDateKey]) {
            dailyData[orderDateKey].orders++;
            dailyData[orderDateKey].revenue += orderValue;
            dailyData[orderDateKey].count++;
          }

          // Customer statistics (order count)
          partyStats[order.partyName] = (partyStats[order.partyName] || 0) + 1;
          // Customer revenue statistics
          partyRevenueStats[order.partyName] = (partyRevenueStats[order.partyName] || 0) + orderValue;

          // Salesperson statistics (order count)
          if (order.createdBy) {
            salespersonStats[order.createdBy] = (salespersonStats[order.createdBy] || 0) + 1;
            salespersonRevenueStats[order.createdBy] = (salespersonRevenueStats[order.createdBy] || 0) + orderValue;
          }
          // Also count orders assigned to salespeople
          if (order.assignedTo && order.assignedTo !== order.createdBy) {
            salespersonStats[order.assignedTo] = (salespersonStats[order.assignedTo] || 0) + 1;
            salespersonRevenueStats[order.assignedTo] = (salespersonRevenueStats[order.assignedTo] || 0) + orderValue;
          }

          // Status counts
          statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;

          // Monthly data
          const month = order.createdAt.toDate().toLocaleString('default', { month: 'short' });
          if (!monthlyData[month]) {
            monthlyData[month] = { orders: 0, revenue: 0 };
          }
          monthlyData[month].orders += 1;
          monthlyData[month].revenue += orderValue;

          // Track last order date for each customer
          if (order.partyName && order.createdAt) {
            const prev = partyLastOrderMap[order.partyName];
            const currDate = order.createdAt.toDate();
            if (!prev || currDate > prev) {
              partyLastOrderMap[order.partyName] = currDate;
            }
          }
        });

        // Sort orders by createdAt in memory
        orders.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());

        // Calculate total revenue and average order value
        const totalRevenue = orders.reduce((sum, order) => sum + Number(order.orderValue || 0), 0);
        const averageOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;

        // Calculate period averages
        const currentMonthAOV = currentMonthOrders > 0 ? currentMonthRevenue / currentMonthOrders : 0;
        const previousMonthAOV = previousMonthOrders > 0 ? previousMonthRevenue / previousMonthOrders : 0;

        // Prepare daily data arrays for sparklines
        const dailyOrders = Object.values(dailyData).map(d => d.orders);
        const dailyRevenue = Object.values(dailyData).map(d => d.revenue);
        const dailyAOV = Object.values(dailyData).map(d => d.count > 0 ? d.revenue / d.count : 0);

        // Sort and get top parties by order count
        const sortedParties = Object.entries(partyStats)
          .sort(([, a], [, b]) => b - a);
        const topParties = sortedParties.slice(0, 5);

        // Sort and get top parties by revenue
        const sortedRevenueParties = Object.entries(partyRevenueStats)
          .sort(([, a], [, b]) => b - a);
        const topRevenueParties = sortedRevenueParties.slice(0, 5);

        // Sort and get top salespeople by order count
        const sortedSalespeople = Object.entries(salespersonStats)
          .sort(([, a], [, b]) => b - a);
        const topSalespeople = sortedSalespeople.slice(0, 5);

        // Sort and get top salespeople by revenue
        const sortedRevenueSalespeople = Object.entries(salespersonRevenueStats)
          .sort(([, a], [, b]) => b - a);
        const topRevenueSalespeople = sortedRevenueSalespeople.slice(0, 5);

        // Prepare monthly data for charts
        const months = Object.keys(monthlyData).sort((a, b) => {
          const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
          const currentMonth = monthOrder[new Date().getMonth()];
          
          // If one is current month, put it last
          if (a === currentMonth && b !== currentMonth) return 1;
          if (b === currentMonth && a !== currentMonth) return -1;
          
          // Otherwise sort chronologically
          return monthOrder.indexOf(a) - monthOrder.indexOf(b);
        });

        const monthlyOrders = months.map(month => monthlyData[month].orders);
        const monthlyRevenue = months.map(month => monthlyData[month].revenue);

        // Prepare customer last order dates list
        const partyLastOrderDates = Object.entries(partyLastOrderMap)
          .sort((a, b) => a[1] - b[1]) // oldest first
          .slice(0, 5);

        setAnalytics({
          topParties,
          topSalespeople,
          totalOrders: orders.length,
          totalRevenue,
          averageOrderValue,
          monthlyOrders,
          monthlyRevenue,
          months,
          partyLastOrderDates,
          topRevenueParties,
          topRevenueSalespeople,
          previousMonthOrders,
          previousMonthRevenue,
          previousMonthAOV,
          currentMonthOrders,
          currentMonthRevenue,
          currentMonthAOV,
          dailyOrders,
          dailyRevenue,
          dailyAOV,
          ordersRaw: orders
        });
      } catch (err) {
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };
    if (organizationId) {
      fetchAnalytics();
    }
  }, [organizationId]);

  return { analytics, userMap, loading, error };
} 